import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ShoppingBag, Search, Instagram, MessageCircle, Truck, CreditCard,
  Sparkles, Gift, Droplet, ArrowRight, Lock, LayoutGrid,
} from 'lucide-react';

import Logo from './components/Logo';
import ProductCard from './components/ProductCard';
import ProductDetailDrawer from './components/ProductDetailDrawer';
import CartDrawer from './components/CartDrawer';
import FragranceFinder from './components/FragranceFinder';
import FAQ from './components/FAQ';
import Success from './pages/Success';
import Cancel from './pages/Cancel';

import { PRODUCTS, CATEGORIES } from './data/products';
import { SIZES, CURRENCY_SYMBOL, BRAND, waGeneral } from './config/pricing';

const FEATURED_IDS = [
  'aurora-mist', 'malik-gold', 'princess', 'mufasa',
  'black-cherry-noir', 'tonka-oud', 'shadow-man', 'sugar-rouge',
];

const PRODUCT_BY_ID = new Map(PRODUCTS.map((p) => [p.id, p]));
const SIZE_BY_ID = new Map(SIZES.map((s) => [s.id, s]));

const readStoredArray = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const cleanCartItems = (items) => items.map((item) => {
  const product = PRODUCT_BY_ID.get(item?.productId);
  const size = SIZE_BY_ID.get(item?.sizeId);
  const qty = Math.max(1, Math.min(99, Number.parseInt(item?.qty, 10) || 0));
  if (!product || !size || qty < 1) return null;
  return {
    key: `${product.id}__${size.id}`,
    productId: product.id,
    sizeId: size.id,
    name: product.name,
    image: product.image,
    sizeLabel: size.label,
    price: size.price,
    qty,
  };
}).filter(Boolean);

const cleanRecentIds = (ids) => {
  const seen = new Set();
  return ids.filter((id) => {
    if (typeof id !== 'string' || !PRODUCT_BY_ID.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).slice(0, 4);
};

function useCart() {
  const [items, setItems] = useState(() => cleanCartItems(readStoredArray('ma-cart')));
  useEffect(() => { localStorage.setItem('ma-cart', JSON.stringify(items)); }, [items]);

  const add = useCallback((product, size, qty) => {
    setItems((prev) => {
      const amount = Math.max(1, Math.min(99, Number.parseInt(qty, 10) || 1));
      const key = `${product.id}__${size.id}`;
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => i.key === key ? { ...i, qty: Math.min(99, i.qty + amount) } : i);
      return [...prev, {
        key, productId: product.id, sizeId: size.id,
        name: product.name, image: product.image, sizeLabel: size.label, price: size.price, qty: amount,
      }];
    });
  }, []);
  const setQty = useCallback((key, qty) =>
    setItems((prev) => prev.map((i) => (
      i.key === key ? { ...i, qty: Math.max(1, Math.min(99, Number.parseInt(qty, 10) || 1)) } : i
    ))), []);
  const remove = useCallback((key) =>
    setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);
  const count = items.reduce((n, i) => n + i.qty, 0);

  return { items, add, setQty, remove, clear, count };
}

function Hero({ onShop }) {
  return (
    <section className="hero">
      <div className="container hero-grid">
        <div>
          <span className="eyebrow"><Sparkles size={12} /> Luxury Oud · London</span>
          <h1>
            Luxury Oud Oils <br />
            <span className="accent">Crafted for Presence.</span>
          </h1>
          <p className="lede">
            Concentrated 12ml oud oils from our London atelier. Long lasting,
            gift-ready, unmistakably yours.
          </p>
          <div className="hero-cta">
            <button className="btn btn-gold" onClick={onShop}>
              Shop 12ml Oud Oils <ArrowRight size={14} />
            </button>
            <a className="btn btn-ghost" href={waGeneral()} target="_blank" rel="noopener noreferrer">
              <MessageCircle size={14} /> Order on WhatsApp
            </a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-photo">
            <img
              src="/products/aurora-mist.webp"
              alt="Malik's Aroma oud oil bottle with smoking oud wood"
              width="720"
              height="900"
              loading="eager"
              decoding="async"
            />
            <span className="hero-photo-caption">Oud Parfum · London</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    '12ml Oud Oil · £30', 'Free UK Delivery', 'Secure Card Checkout', 'London Atelier',
  ];
  return (
    <div className="trust-strip" aria-label="Shopping information">
      <div className="container trust-strip-track">
        {items.map((item) => <span key={item}>{item}</span>)}
      </div>
    </div>
  );
}

const MOODS = [
  { cat: 'Signature Oud', tag: 'The Classics', desc: 'Deep, traditional oud character.' },
  { cat: 'Sweet & Warm', tag: 'Soft Indulgence', desc: 'Vanilla, amber and caramel warmth.' },
  { cat: 'Dark & Bold', tag: 'After Dark', desc: 'Smoke, leather and rich spice.' },
  { cat: 'Fresh & Bright', tag: 'Clean & Daily', desc: 'Citrus, air and light woods.' },
  { cat: 'Floral & Elegant', tag: 'Refined Florals', desc: 'Rose, jasmine and soft blossom.' },
  { cat: 'Soft & Clean', tag: 'Quiet Luxury', desc: 'Musk, powder and skin-soft warmth.' },
];

function ShopByMood({ activeCat, onPick }) {
  return (
    <section className="section dark" id="shop-by-mood">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow"><Sparkles size={12} /> Find Your Vibe</span>
          <h2>Shop by mood</h2>
          <p className="lede">Choose a feeling — we'll take you straight to the right oud.</p>
        </div>
        <div className="mood-grid">
          {MOODS.map((m) => (
            <button
              key={m.cat}
              type="button"
              className={`mood-card ${activeCat === m.cat ? 'active' : ''}`}
              onClick={() => onPick(m.cat)}
            >
              <span className="mood-tag">{m.tag}</span>
              <span className="mood-name">{m.cat}</span>
              <span className="mood-desc">{m.desc}</span>
              <span className="mood-go">{activeCat === m.cat ? 'Selected' : 'Explore'} <ArrowRight size={13} /></span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedCollection({ onAdd, onOpen }) {
  const featured = FEATURED_IDS.map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean);
  return (
    <section className="section dark" id="featured">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">The House Signatures</span>
          <h2>Featured fragrances</h2>
          <p className="lede">A curated selection — bold oud, sweet warmth, smoky depth, and clean elegance.</p>
        </div>
        <div className="product-grid product-rail">
          {featured.map((p) => <ProductCard key={p.id} product={p} onAdd={onAdd} onOpen={onOpen} />)}
        </div>
      </div>
    </section>
  );
}

function FullCollection({ onAdd, onOpen, cat, onCat, q, onQuery, activeMoodLine, onClearMood }) {
  const list = useMemo(() => {
    return PRODUCTS.filter((p) => {
      if (cat === 'All') return true;
      if (cat === 'Gift Ready') return p.giftReady;
      return p.category === cat;
    }).filter((p) => {
      if (!q.trim()) return true;
      const hay = `${p.name} ${p.category} ${p.profile} ${p.bestFor} ${p.notes.join(' ')}`.toLowerCase();
      return hay.includes(q.toLowerCase().trim());
    });
  }, [cat, q]);

  return (
    <section className="section cream" id="collection">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">The Atelier</span>
          <h2>The full collection</h2>
          <p className="lede">Filter, search, or browse the house of oud.</p>
        </div>
        <div className="toolbar">
          <div className="filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                className={`chip ${cat === c ? 'active' : ''}`}
                onClick={() => onCat(c)}
              >{c}</button>
            ))}
          </div>
          <div className="search">
            <Search size={14} />
            <input
              aria-label="Search products"
              placeholder="Search by name, note, or mood"
              value={q}
              onChange={(e) => onQuery(e.target.value)}
            />
          </div>
        </div>

        {activeMoodLine && (
          <div className="collection-status">
            <span>{activeMoodLine}</span>
            <button type="button" onClick={onClearMood}>Show all</button>
          </div>
        )}

        <p className="collection-count">
          <strong>{list.length}</strong>{' '}
          {list.length === 1 ? 'fragrance' : 'fragrances'}
          {cat === 'All' ? ' · the full house' : ` · ${cat}`}
        </p>

        {list.length === 0 ? (
          <div className="empty-state">
            <h3>No matching fragrances</h3>
            <p>Try another note, or return to the full collection.</p>
            <button type="button" className="btn btn-gold btn-sm" onClick={onClearMood}>
              Show all fragrances
            </button>
          </div>
        ) : (
          <div className="product-grid">
            {list.map((p) => (
              <div id={`product-${p.id}`} key={p.id}>
                <ProductCard product={p} onAdd={onAdd} onOpen={onOpen} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RecentlyViewed({ ids, onOpen }) {
  const items = ids.map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean);
  if (!items.length) return null;

  return (
    <section className="recently-viewed" aria-label="Recently viewed products">
      <div className="container">
        <div className="recent-head">
          <span>Recently viewed</span>
        </div>
        <div className="recent-rail">
          {items.map((p) => (
            <button key={p.id} type="button" className="recent-card" onClick={() => onOpen(p)}>
              <span className="recent-thumb">
                <img src={p.image} alt="" loading="lazy" width="96" height="108" decoding="async" />
              </span>
              <span>
                <strong>{p.name}</strong>
                <small>{p.category}</small>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function MobileShopBar({ count, onShop, onFinder, onCart }) {
  return (
    <div className="mobile-shopbar" aria-label="Shop shortcuts">
      <button type="button" onClick={onShop}>
        <LayoutGrid size={15} />
        <span>Shop</span>
      </button>
      <button type="button" onClick={onFinder}>
        <Sparkles size={15} />
        <span>Finder</span>
      </button>
      <button type="button" className={count > 0 ? 'active' : ''} onClick={onCart}>
        <ShoppingBag size={15} />
        <span>Cart</span>
        {count > 0 && <em>{count}</em>}
      </button>
    </div>
  );
}

function HowToWear() {
  const steps = [
    { n: '01', h: 'Start with restraint', t: 'Begin with 1 to 2 drops. Oud is concentrated — a little carries far.' },
    { n: '02', h: 'Pulse points', t: 'Wrists, behind the ears, base of the neck. Warm skin is the canvas.' },
    { n: '03', h: 'Let it settle', t: 'Do not rub. Let the oil bloom into your skin for 30 seconds.' },
    { n: '04', h: 'Build slowly', t: 'Add a drop if needed. Less is luxury. Memorable, not loud.' },
  ];
  return (
    <section className="section dark" id="how-to-wear">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">House Guide</span>
          <h2>How to wear oud</h2>
          <p className="lede">A short ritual that turns fragrance into presence.</p>
        </div>
        <div className="cards-row cols-4">
          {steps.map((s) => (
            <div key={s.n} className="luxe-card">
              <span className="step-num">{s.n}</span>
              <h3>{s.h}</h3>
              <p>{s.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyMaliks() {
  const items = [
    { i: <Droplet size={18} />, h: 'Long lasting oils', t: 'Concentrated oud that wears for hours, not minutes.' },
    { i: <Sparkles size={18} />, h: 'Alcohol-free feel', t: 'Smooth, skin-friendly application without sharp dry-down.' },
    { i: <Gift size={18} />, h: 'Gift-ready presentation', t: 'Every order arrives prepared for the occasion.' },
    { i: <Truck size={18} />, h: 'Free UK delivery', t: 'Carefully packed and shipped on every order.' },
    { i: <CreditCard size={18} />, h: 'Secure card payment', t: 'Stripe-powered checkout — your card details stay private.' },
    { i: <MessageCircle size={18} />, h: 'WhatsApp support', t: 'Direct from London — guidance, gift wrapping, custom orders.' },
  ];
  return (
    <section className="section" id="why">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Why Malik's Aroma</span>
          <h2>Luxury without noise</h2>
        </div>
        <div className="cards-row cols-3">
          {items.map((x, i) => (
            <div key={i} className="luxe-card">
              <span className="gold" style={{ display: 'inline-flex', marginBottom: 10 }}>{x.i}</span>
              <h3>{x.h}</h3>
              <p>{x.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const reviews = [
    { t: 'Strong, smooth and lasts for hours. The packaging makes it feel like a proper gift.', n: 'A.K. · London' },
    { t: 'Ordered through WhatsApp and got help choosing the right scent. Really clean service.', n: 'H.M. · Manchester' },
    { t: 'Black Cherry Noir is unreal. Sweet, smoky and different.', n: 'S.R. · Birmingham' },
    { t: 'Malik Gold is fresh, warm and easy to wear every day.', n: 'Y.A. · Leicester' },
    { t: 'Bought one as a gift and ended up ordering another for myself.', n: 'F.D. · London' },
  ];
  return (
    <section className="section" id="reviews">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Client Letters</span>
          <h2>Words from those who wear it</h2>
        </div>
        <div className="cards-row cols-3 reviews-rail">
          {reviews.map((r, i) => (
            <div key={i} className="review">
              <div className="stars">★ ★ ★ ★ ★</div>
              <p>&ldquo;{r.t}&rdquo;</p>
              <small>{r.n}</small>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FooterPolicies() {
  const items = [
    {
      h: 'Delivery',
      t: 'Free UK delivery on every order. Orders are packed carefully and sent to the address confirmed at checkout.',
    },
    {
      h: 'Returns',
      t: 'If there is an issue with your order, contact us within 14 days. Unused items can be reviewed for return or replacement.',
    },
    {
      h: 'Privacy',
      t: 'We only use order and contact details to process purchases, arrange delivery, and respond to support messages.',
    },
    {
      h: 'Terms & Payment',
      t: 'Card payments are handled securely by Stripe. WhatsApp ordering is available if checkout is still being set up.',
    },
  ];

  return (
    <div className="footer-policies" id="policies">
      {items.map((item) => (
        <div key={item.h} className="policy-item">
          <h4>{item.h}</h4>
          <p>{item.t}</p>
        </div>
      ))}
    </div>
  );
}

function FinalCTA({ onShop }) {
  return (
    <section className="final-cta" id="cta">
      <div className="container">
        <div className="divider">Malik's Aroma</div>
        <h2 style={{ marginTop: 24 }}>Find your signature scent</h2>
        <p>Browse the full collection, pay securely by card, or message us to find your scent.</p>
        <div className="actions">
          <button className="btn btn-gold" onClick={onShop}>Shop Collection</button>
          <a className="btn btn-wa" href={waGeneral()} target="_blank" rel="noopener noreferrer">
            <MessageCircle size={14} /> Message on WhatsApp
          </a>
          <a className="btn btn-ghost" href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer">
            <Instagram size={14} /> Visit Instagram
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <div style={{ marginBottom: 16 }}>
              <Logo full />
            </div>
            <p className="muted" style={{ fontSize: '.88rem', maxWidth: 320 }}>
              Premium oud oils, refined for presence. Free UK delivery on every order.
            </p>
          </div>
          <div>
            <h4>Size & Price</h4>
            <ul>
              {SIZES.map((s) => <li key={s.id}>{s.label} — {CURRENCY_SYMBOL}{s.price}</li>)}
            </ul>
          </div>
          <div>
            <h4>Contact</h4>
            <ul>
              <li><a href={waGeneral()} target="_blank" rel="noopener noreferrer">WhatsApp · {BRAND.whatsappDisplay}</a></li>
              <li><a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer">Instagram · @{BRAND.instagram}</a></li>
            </ul>
          </div>
          <div>
            <h4>House</h4>
            <ul>
              <li><a href="#collection">Collection</a></li>
              <li><a href="#finder">Fragrance Finder</a></li>
              <li><a href="#faq">FAQ</a></li>
              <li><a href="#policies">Delivery & Returns</a></li>
            </ul>
          </div>
        </div>
        <FooterPolicies />
        <div className="legal">
          <span>© {new Date().getFullYear()} Malik's Aroma · London</span>
          <span><Lock size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />Secure card payments powered by Stripe.</span>
        </div>
      </div>
    </footer>
  );
}

function HomePage() {
  const cart = useCart();
  const [open, setOpen] = useState(false);
  const [checkoutState, setCheckoutState] = useState('idle');
  const [checkoutError, setCheckoutError] = useState('');
  const [activeCat, setActiveCat] = useState('All');
  const [query, setQuery] = useState('');
  const [activeFinderMood, setActiveFinderMood] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [recentIds, setRecentIds] = useState(() => cleanRecentIds(readStoredArray('ma-recent')));

  useEffect(() => {
    localStorage.setItem('ma-recent', JSON.stringify(recentIds));
  }, [recentIds]);

  const openProduct = useCallback((product) => {
    setSelectedProduct(product);
    setRecentIds((prev) => [product.id, ...prev.filter((id) => id !== product.id)].slice(0, 4));
  }, []);

  const handleAdd = (product, size, qty) => {
    cart.add(product, size, qty);
    setOpen(true);
  };

  const scrollToCollection = () => {
    document.getElementById('collection')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Pick a mood → set the collection filter, then glide to the collection
  const handleMood = (cat) => {
    setActiveFinderMood(null);
    setActiveCat(cat);
    setQuery('');
    setTimeout(scrollToCollection, 70);
  };

  const handleFinderMood = (mood) => {
    setActiveFinderMood(mood.id);
    setActiveCat(mood.category);
    setQuery('');
    setTimeout(scrollToCollection, 70);
  };

  const clearFinderMood = () => {
    setActiveFinderMood(null);
    setActiveCat('All');
    setQuery('');
    setTimeout(scrollToCollection, 70);
  };

  const scrollToFinder = () => {
    document.getElementById('finder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckout = async () => {
    setCheckoutState('loading');
    setCheckoutError('');
    try {
      const res = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.items.map((i) => ({ productId: i.productId, sizeId: i.sizeId, qty: i.qty })),
        }),
      });

      const contentType = res.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Card checkout is unavailable right now. Please order on WhatsApp or try again shortly.');
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Card checkout is unavailable right now. Please order on WhatsApp or try again shortly.');
        }
        if (res.status === 503) {
          throw new Error('Card checkout is unavailable right now. Please order on WhatsApp or try again shortly.');
        }
        if (res.status === 400) {
          throw new Error('Please refresh your bag or order on WhatsApp.');
        }
        throw new Error('Card checkout is temporarily unavailable. Please order on WhatsApp.');
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('Card checkout is unavailable right now. Please order on WhatsApp or try again shortly.');
    } catch (e) {
      setCheckoutError(e.message || 'Card checkout is temporarily unavailable. Please order on WhatsApp.');
      setCheckoutState('error');
      setTimeout(() => setCheckoutState('idle'), 4000);
    }
  };

  const onShop = () => {
    setActiveFinderMood(null);
    setActiveCat('All');
    setQuery('');
    setTimeout(scrollToCollection, 70);
  };

  const activeFinder = activeFinderMood
    ? {
      fresh: 'Showing fresh, clean scents',
      sweet: 'Showing warm, sweet scents',
      dark: 'Showing dark, smoky scents',
      oud: 'Showing signature oud scents',
      date: 'Showing evening statement scents',
      everyday: 'Showing easy everyday scents',
      him: 'Showing bold gift ideas',
      her: 'Showing elegant gift ideas',
    }[activeFinderMood]
    : '';

  return (
    <>
      <nav className="nav">
        <div className="container nav-inner">
          <a href="/" className="nav-brand" aria-label="Malik's Aroma home">
            <Logo />
            <div className="nav-brand-text">
              <span className="nav-brand-name">MALIK'S AROMA</span>
              <span className="nav-brand-sub">Luxury Oud · London</span>
            </div>
          </a>
          <div className="nav-links">
            <a href="#featured">Featured</a>
            <a href="#collection">Collection</a>
            <a href="#finder">Finder</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="nav-actions">
            <a className="icon-btn nav-social" href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a className="icon-btn nav-social" href={waGeneral()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <MessageCircle size={18} />
            </a>
            <button className="icon-btn nav-cart" onClick={() => setOpen(true)} aria-label={`Open cart, ${cart.count} items`}>
              <ShoppingBag size={18} />
              {cart.count > 0 && <span className="cart-pill" key={cart.count}>{cart.count}</span>}
            </button>
          </div>
        </div>
      </nav>

      <Hero onShop={onShop} />
      <TrustBar />
      <ShopByMood activeCat={activeCat} onPick={handleMood} />
      <FeaturedCollection onAdd={handleAdd} onOpen={openProduct} />
      <FullCollection
        onAdd={handleAdd}
        onOpen={openProduct}
        cat={activeCat}
        onCat={(cat) => {
          setActiveFinderMood(null);
          setActiveCat(cat);
        }}
        q={query}
        onQuery={setQuery}
        activeMoodLine={activeFinder}
        onClearMood={clearFinderMood}
      />
      <RecentlyViewed ids={recentIds} onOpen={openProduct} />
      <FragranceFinder onOpen={openProduct} onApply={handleFinderMood} />
      <HowToWear />
      <WhyMaliks />
      <Reviews />
      <FAQ />
      <FinalCTA onShop={onShop} />
      <Footer />

      <MobileShopBar count={cart.count} onShop={onShop} onFinder={scrollToFinder} onCart={() => setOpen(true)} />

      <ProductDetailDrawer
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAdd={handleAdd}
      />

      <CartDrawer
        open={open}
        onClose={() => setOpen(false)}
        items={cart.items}
        setQty={cart.setQty}
        remove={cart.remove}
        onCheckout={handleCheckout}
        checkoutState={checkoutState}
        checkoutError={checkoutError}
        onShop={onShop}
      />
    </>
  );
}

export default function App() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path === '/success' || path === '/success/') return <Success />;
  if (path === '/cancel' || path === '/cancel/') return <Cancel />;
  return <HomePage />;
}
